import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  DownloadOutlined as DownloadIcon,
  FolderOffOutlined as EmptyIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { useDocumentArchive } from "../../Contexts/DocumentArchiveContext";
import { useShipSettings } from "../../Contexts/ShipSettingsContext";
import { downloadPrintDocument } from "../../print-v2";

const formatGeneratedAt = (value) =>
  value ? new Date(value).toLocaleString() : "Unknown time";

const DocumentArchive = () => {
  const { shipData } = useShipSettings();
  const { listGeneratedDocuments, deleteGeneratedDocument, clearGeneratedDocuments } = useDocumentArchive();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDocuments = useCallback(async () => {
    if (!shipData?.id) {
      setDocuments([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      setDocuments(await listGeneratedDocuments(shipData.id));
    } finally {
      setLoading(false);
    }
  }, [listGeneratedDocuments, shipData?.id]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const removeDocument = async (documentId) => {
    if (!window.confirm("Delete this local document archive entry?")) return;
    await deleteGeneratedDocument(documentId);
    await loadDocuments();
  };

  const clearArchive = async () => {
    if (!shipData?.id || !window.confirm("Delete every locally archived document for this vessel?")) return;
    await clearGeneratedDocuments(shipData.id);
    await loadDocuments();
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 960, mx: "auto" }}>
      <Typography variant="h4" fontWeight={800} gutterBottom>
        Document Archive
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Documents are stored on this bridge computer for up to 365 days. The archive retains at most 200 documents per vessel.
      </Typography>

      {documents.length > 0 && (
        <Button color="error" startIcon={<DeleteIcon />} onClick={clearArchive} sx={{ mb: 2 }}>
          Clear Local Archive
        </Button>
      )}

      {loading ? (
        <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box>
      ) : documents.length === 0 ? (
        <Box sx={{ py: 8, textAlign: "center", color: "text.secondary" }}>
          <EmptyIcon sx={{ fontSize: 48, mb: 1 }} />
          <Typography>No locally archived documents for this vessel.</Typography>
        </Box>
      ) : (
        <Stack divider={<Divider flexItem />}>
          {documents.map((document) => (
            <Box
              key={document.id}
              sx={{ py: 2, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
            >
              <Box sx={{ flex: "1 1 360px" }}>
                <Typography fontWeight={700}>{document.documentTitle}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatGeneratedAt(document.generatedAt)} · {document.templateId}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Voyage: {document.voyageId || "Not linked"} · Trip: {document.tripId || "Not linked"}
                </Typography>
              </Box>
              <Chip label={document.deliveryStatus.replaceAll("_", " ")} size="small" />
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => downloadPrintDocument({ title: document.documentTitle, html: document.html })}
              >
                Download HTML
              </Button>
              <Button color="error" variant="text" onClick={() => removeDocument(document.id)}>
                Delete
              </Button>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default DocumentArchive;
